import { FC, useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import { useDropzone } from 'react-dropzone';

import DocumentsBgImage from '../../../assets/images/documents_bg_pic.png';
import CustomButton from '../../../common/CustomButton';
import FilterIcon from '../../../assets/icons/user-info/Filter, Sort.png';
import CloudIcon from '../../../assets/icons/cloud-upload-outlined.png';
import DeleteIcon from '../../../assets/icons/documents/Trash, Delete, Bin.png';
import PdfIcon from '../../../assets/icons/documents/pdf_OneDrive_icon 1.png';
import DownloadIcon from '../../../assets/icons/Download, Save, Upload.png';
import ManageTags from '../../../components/ManageTags/ManageTags';
import Tags from '../../../components/Tags';

import { useGetTagsQuery } from '../../../store/services/tagsApi';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { setTags, Tag } from '../../../store/slices/tagsSlice';
import CustomMultiselect from '../../../common/CustomMultiselect';
import { useAddDocumetMutation, useGetDocumentsQuery, useUpdateDocumetMutation } from '../../../store/services/documentsApi';
import CustomInput from '../../../common/CustomInput';
import { Attachment, setDocumentsData } from '../../../store/slices/documentsSlice';

import styles from './Documents.module.scss';

const Documents: FC = () => {
  const dispatch = useAppDispatch();
  const { tags } = useAppSelector((state) => state.tags);
  const { documents } = useAppSelector((state) => state.documents);
  const { data: tagsRes } = useGetTagsQuery({});
  const {data: documetsRes, refetch} = useGetDocumentsQuery({});
  const [addDocumetMutation] = useAddDocumetMutation();
  const [updateDocumetMutation] = useUpdateDocumetMutation();

  const [selected, setSelected] = useState('personal');
  const [openFilters, setOpenFilters] = useState(false);
  const [openManageFilters, setOpenManageFilters] = useState(false);
  const [openModal, setModalOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [isEditing, setIsEditing] = useState(false);
  // const [documents, setDocuments] = useState<any[]>([]);
  const [editingDocumentId, setEditingDocumentId] = useState<number | null>(null);
  const [ isSuccess, setIsSuccess] = useState<string | null>(null);
  const [ isFail, setIsFail] = useState<string | null>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [tagsOptions, setTagsOptions] = useState<Tag[]>([]);
  const [documentName, setDocumentName] = useState<string>('');

  useEffect(() => {
    dispatch(setDocumentsData(documetsRes));
  }, [documetsRes])

  useEffect(() => {
    if (tagsRes) {
      dispatch(setTags(tagsRes));
    }
  }, [tagsRes]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles((prev) => [...prev, ...acceptedFiles])
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleRemoveFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
  }

  const toggleFilters = (newOpen: boolean) => () => {
    setOpenFilters(newOpen);
  };

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleModal = (editing: boolean = false) => {
    setModalOpen((prev) => !prev);
    setIsEditing(editing);

    if (openModal) {
      setEditingDocumentId(null);
      setDocumentName('');
      setFiles([]);
      setTagsOptions([]);
    }
  };

  const handleToggleChange = (_: React.MouseEvent<HTMLElement>, newSelection: string | null) => {
    if (newSelection !== null) setSelected(newSelection);
  };

  const handleAddDocument = async () => {
    if (isEditing && editingDocumentId !== null) {
      try {
        const formData = new FormData();
        formData.append("name", documentName);
      
        tagsOptions?.forEach((tag) => {
          formData.append("tags", String(tag.id));
        });

        files.forEach((file) => {
          formData.append("attachments", file);
        });
      
        await updateDocumetMutation({ id: editingDocumentId, formData });

        refetch();
        setIsSuccess("Edit document success");
      } catch (error) {
        console.error(error);
        setIsFail("Edit document fail");
      }
    } else {
      try {
        const formData = new FormData();
        formData.append("name", documentName);

        tagsOptions?.forEach((tag) => {
          formData.append("tags", String(tag.id));
        });

        files.forEach((file) => {
          formData.append("attachments", file);
        });

        const response = await addDocumetMutation(formData);

        dispatch(setDocumentsData([...(documents || []), response.data]));
  
        setIsSuccess("Add document success");
      } catch (error) {
        console.error();
        setIsFail('Add document fail');
      }
    }

    setEditingDocumentId(null);
    setFiles([]);
    setIsEditing(false);
    setModalOpen(false);
  };

  const handleEditDocument = (documentId: number) => {
    const documentToEdit = documents?.find(doc => doc.id === documentId);

    setDocumentName(documentToEdit?.name || "");

    setTagsOptions(
      documentToEdit?.tags?.map((tag: any) =>
        typeof tag === 'string' ? { id: tag, name: tag, color: '#000' } : tag
      ) || []
    );

    setFiles(
      (documentToEdit?.attachments || [])
        .map((attachment: Attachment) =>
          attachment.content
            ? new File(
                [attachment.content],
                attachment.content?.split('/').pop() || 'unknown',
                { type: 'application/octet-stream' }
              )
            : null
        )
        .filter((file): file is File => file !== null)
    );
  
    setEditingDocumentId(documentId);
    handleModal(true);
  };

  // const handleDeleteDocument = () => {
  //   if (editingDocumentIndex !== null) {
  //     setDocuments((prev) =>
  //       prev.filter((_, index) => index !== editingDocumentIndex)
  //     );
  //     setEditingDocumentIndex(null);
  //     setFiles([]);
  //     setIsEditing(false);
  //     setModalOpen(false);
  //   }
  // };

  return (
    <>
      <img src={DocumentsBgImage} alt='bg' className={styles.bgImg} />
      <Dialog
        open={openModal}
        onClose={() => handleModal(false)}
        maxWidth="md"
        fullWidth
        sx={{
          maxWidth: '546px',
          margin: 'auto',
        }}
      >
        <DialogTitle
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography variant='h5Bold'>
            {isEditing ? 'National ID' : 'Add Document'}
          </Typography>
          <CloseIcon onClick={() => handleModal(false)} sx={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            '&:hover': {
              cursor: 'pointer'
            }
          }} />
        </DialogTitle>
        <DialogContent>
          <CustomInput
            inputLabel='Name'
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
          />
          <CustomMultiselect
            tags={tags}
            tagsOptions={tagsOptions}
            setTagsOptions={setTagsOptions}
          />
          <br />
          <Typography variant='h11Semibold'>Attach Documents</Typography>
          <Box
            {...getRootProps()}
            style={{
              padding: "24px",
              marginBottom: "16px",
              border: "2px dashed",
              borderRadius: '20px',
              borderColor: isDragActive ? "#1976d2" : "#bdbdbd",
              backgroundColor: isDragActive ? "rgba(25, 118, 210, 0.08)" : "#fff",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <input {...getInputProps()} />
            <img src={CloudIcon} alt='cloud' className={styles.cloudIcon} />
            <Typography variant="h12Semibold" gutterBottom>
              Select your file
            </Typography>
            <Typography variant="h13" color="textSecondary">
              png, pdf, jpg, docx accepted
            </Typography>
          </Box>

          {files.length > 0 && (
            <Box>
              <List sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}>
                {files.map((file, index) => (
                  <ListItem
                    key={index}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleRemoveFile(index)}>
                        <img src={DeleteIcon} alt='delete' />
                      </IconButton>
                    }
                    sx={{
                      border: '1px solid #DADFE8',
                      borderRadius: '8px',
                      padding: 0
                    }}
                  >
                    <ListItemIcon>
                      <img src={PdfIcon} alt='pdf icon' />
                    </ListItemIcon>
                    <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(2)} KB`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px 20px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyItems: 'center',
              alignContent: 'center',
              gap: '10px',
            }}
          >
            {isEditing ? (
              <>
                {/* <CustomButton variant='outlined' onClick={handleDeleteDocument}>
                  Delete Document
                </CustomButton> */}
                <CustomButton variant='outlined'>
                  Delete Document
                </CustomButton>
                <CustomButton
                  variant='contained'
                  onClick={handleAddDocument}
                  disabled={files.length === 0 || tagsOptions.length === 0 || !documentName}
                >
                  Save Changes
                </CustomButton>
              </>
            ) : (
              <>
                <CustomButton variant='outlined' onClick={handleModal}>
                  Cancel
                </CustomButton>
                <CustomButton
                  variant='contained'
                  onClick={handleAddDocument}
                  disabled={files.length === 0 || tagsOptions.length === 0 || !documentName}
                >
                  Add Document
                </CustomButton>
              </>
            )}
          </Box>
        </DialogActions>
      </Dialog>
      <Box className={styles.documents}>
        <Box className={styles.documentsHeader}>
          <Box className={styles.documentsHeaderLeft}>
            <Typography variant='h4Bold'>Documents</Typography>
            <Typography variant='h10' sx={{ color: '#5E6A81' }}>
              List of your documents and assets for travels
            </Typography>
          </Box>

          <CustomButton
            variant='contained'
            onClick={() => handleModal(false)}
            customClass={styles.customButton}
          >
            + Create new
          </CustomButton>
        </Box>

        <Box className={styles.documentsNavWrapper}>
          <ToggleButtonGroup
            value={selected}
            exclusive
            onChange={handleToggleChange}
            className={styles.toggleGroup}
          >
            <ToggleButton value="personal" className={`${selected === 'personal' && styles.selected} ${styles.toggleButton}`}>
              {selected === 'personal' ? (
                <Typography variant='h11Bold'>Personal</Typography>
              ) : (
                <Typography variant='h11Semibold'>Personal</Typography>
              )}
            </ToggleButton>
            <ToggleButton value="all" className={`${selected === 'all' && styles.selected} ${styles.toggleButton}`}>
              {selected === 'all' ? (
                <Typography variant='h11Bold'>All</Typography>
              ) : (
                <Typography variant='h11Semibold'>All</Typography>
              )}
            </ToggleButton>
          </ToggleButtonGroup>

          <Box className={styles.filterIconWrapper} onClick={toggleFilters(true)}>
            <img src={FilterIcon} alt='filter icon' />
            <Typography variant='h10Semibold'>Filters</Typography>
          </Box>
        </Box>

        {selected === 'personal' && (
          <Box className={styles.contentWrapper}>
            <Box className={styles.content}>
              {documents?.map((document) => {
                const panelId = `panel-${document.id}`;
                return (
                  <Accordion
                    key={panelId}
                    expanded={expanded === panelId}
                    onChange={handleChange(panelId)}
                    sx={{
                      width: '100%',
                      boxShadow: 'none',
                      '&:not(.Mui-expanded)': {
                        border: '1px solid #BDC9EE',
                        borderRadius: '8px',
                      },
                      '&.Mui-expanded': {
                        border: '1px solid #BDC9EE',
                        borderRadius: '8px',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={expanded === panelId ? <RemoveIcon  /> : <AddIcon />}
                      aria-controls={`${panelId}-content`}
                      id={`${panelId}-header`}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column'}}>
                        <Typography variant='h7Bold' component="span">{document.name}</Typography>
                        <Box sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '8px',
                          height: '26px',
                          marginTop: '8px',
                        }}>
                          {tags?.filter(tag => document.tags.some((documentTag: any) => documentTag.id === tag.id)).map(tag => (
                            <Typography key={tag.id} variant="h12Semibold" sx={{ backgroundColor: tag.color, padding: '5px 8px', borderRadius: '16px' }}>
                              {tag.name}
                            </Typography>
                          ))}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box sx={{ border: '1px solid #BDC9EE', borderRadius: '16px', padding: '16px', marginBottom: '10px' }}>
                        <Typography variant='h9Semibold'>Document files</Typography>

                        <Box>
                          <List sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                          }}>
                            {document.attachments.map((attachment: Attachment) => (
                              <ListItem
                                key={attachment.id}
                                secondaryAction={
                                  <a
                                    target="_blank"
                                    href={`${process.env.REACT_APP_API_URL}${attachment.content}`}
                                    download={attachment.content?.split("/").pop()}
                                  >
                                    <IconButton edge="end">
                                      <img src={DownloadIcon} alt="download" />
                                    </IconButton>
                                  </a>
                                }
                                sx={{
                                  border: '1px solid #DADFE8',
                                  borderRadius: '8px',
                                  padding: 0
                                }}
                              >
                                <ListItemIcon>
                                  <img src={PdfIcon} alt='pdf icon' />
                                </ListItemIcon>
                                {/* <ListItemText primary={attachment.content} secondary={`${(file.size / 1024).toFixed(2)} KB`} /> */}
                                <ListItemText primary={attachment.content?.split("/").pop()} />
                              </ListItem>
                            ))}

                          </List>
                        </Box>
                      </Box>
                      <CustomButton variant='outlined' onClick={() => handleEditDocument(document.id)}>
                        Edit
                      </CustomButton>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Box>
        )}
        <ManageTags
          openFilters={openFilters}
          tags={tags}
          toggleFilters={setOpenFilters}
          toggleManageDrawer={setOpenManageFilters}
        />

        <Tags
          openManageFilters={openManageFilters}
          // setTags={setTagsData}
          tags={tags}
          toggleManageDrawer={setOpenManageFilters}
        />
      </Box>
    </>
  )
}

export default Documents;
